import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrganizations, getPortfolioIntelligence } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Building2,
  AlertTriangle,
  Shield,
  TrendingUp,
  TrendingDown,
  Users,
  FileText,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  ChevronRight,
  BarChart3,
  PieChart,
  Activity,
  Target,
  Zap,
  Eye,
  Calendar,
  MapPin,
  Loader2,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Minus
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line
} from 'recharts';

const LEVEL_COLORS = {
  national: { bg: 'bg-purple-500/20', text: 'text-purple-600', border: 'border-purple-500/30' },
  regional: { bg: 'bg-blue-500/20', text: 'text-blue-600', border: 'border-blue-500/30' },
  state: { bg: 'bg-green-500/20', text: 'text-green-600', border: 'border-green-500/30' },
  local: { bg: 'bg-orange-500/20', text: 'text-orange-600', border: 'border-orange-500/30' },
  franchise: { bg: 'bg-cyan-500/20', text: 'text-cyan-600', border: 'border-cyan-500/30' },
  franchisee: { bg: 'bg-teal-500/20', text: 'text-teal-600', border: 'border-teal-500/30' },
};

const RISK_COLORS = {
  low: '#22c55e',
  medium: '#f59e0b',
  high: '#ef4444',
  critical: '#dc2626'
};

const CHART_COLORS = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e'];

export default function NetworkIntelligence() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [portfolioData, setPortfolioData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async () => {
    try {
      const [orgsRes, portfolioRes] = await Promise.all([
        getOrganizations(),
        getPortfolioIntelligence({ days: parseInt(selectedPeriod) })
      ]);
      
      setOrganizations(orgsRes.data || []);
      setPortfolioData(portfolioRes.data);
    } catch (error) {
      console.error('Failed to fetch portfolio data:', error);
      toast.error('Failed to load network intelligence');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Find the user's own organization
  const myOrg = organizations.find(o => o.id === user?.organization_id);
  
  // Count organizations by level
  const orgsByLevel = organizations.reduce((acc, org) => {
    acc[org.level] = (acc[org.level] || 0) + 1;
    return acc;
  }, {});

  // Calculate summary metrics
  const summary = portfolioData?.summary || {};
  const orgMetrics = portfolioData?.organizations || [];
  
  // Sort orgs by risk score (highest first)
  const sortedOrgMetrics = [...orgMetrics].sort((a, b) => 
    (b.risk_score || 0) - (a.risk_score || 0)
  );

  // Prepare chart data
  const incidentsByOrg = orgMetrics.slice(0, 10).map(org => ({
    name: org.name?.substring(0, 15) + (org.name?.length > 15 ? '...' : ''),
    incidents: org.incidents_count || 0,
    // claims: org.claims_count || 0  // Claims hidden
  }));

  const riskDistribution = [
    { name: 'Low Risk', value: orgMetrics.filter(o => (o.risk_score || 0) <= 3).length, color: RISK_COLORS.low },
    { name: 'Medium Risk', value: orgMetrics.filter(o => (o.risk_score || 0) > 3 && (o.risk_score || 0) <= 6).length, color: RISK_COLORS.medium },
    { name: 'High Risk', value: orgMetrics.filter(o => (o.risk_score || 0) > 6 && (o.risk_score || 0) <= 8).length, color: RISK_COLORS.high },
    { name: 'Critical', value: orgMetrics.filter(o => (o.risk_score || 0) > 8).length, color: RISK_COLORS.critical },
  ].filter(d => d.value > 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getRiskColor = (score) => {
    if (score <= 3) return 'text-green-500';
    if (score <= 6) return 'text-yellow-500';
    if (score <= 8) return 'text-orange-500';
    return 'text-red-500';
  };

  const getRiskBadge = (score) => {
    if (score <= 3) return { label: 'Low', color: 'bg-green-500/20 text-green-600' };
    if (score <= 6) return { label: 'Medium', color: 'bg-yellow-500/20 text-yellow-600' };
    if (score <= 8) return { label: 'High', color: 'bg-orange-500/20 text-orange-600' };
    return { label: 'Critical', color: 'bg-red-500/20 text-red-600' };
  };

  const getTrendIcon = (trend) => {
    if (trend > 0) return <ArrowUpRight className="w-4 h-4 text-red-500" />;
    if (trend < 0) return <ArrowDownRight className="w-4 h-4 text-green-500" />;
    return <Minus className="w-4 h-4 text-muted-foreground" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading network intelligence...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="network-intelligence">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center">
              <Activity className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Network Intelligence</h1>
              <p className="text-muted-foreground">
                Aggregate risk overview for {myOrg?.name || 'your organization'}
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-[140px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Network Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-primary" />
              <span className="text-sm text-muted-foreground">Organizations</span>
            </div>
            <p className="text-2xl font-bold">{organizations.length}</p>
            <div className="flex gap-1 mt-2 flex-wrap">
              {Object.entries(orgsByLevel).map(([level, count]) => (
                <Badge key={level} variant="outline" className="text-[10px]">
                  {count} {level}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              <span className="text-sm text-muted-foreground">Total Incidents</span>
            </div>
            <p className="text-2xl font-bold">{summary.total_incidents || 0}</p>
            <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
              {getTrendIcon(summary.incidents_trend)}
              <span>{summary.incidents_trend > 0 ? '+' : ''}{summary.incidents_trend || 0}% vs prior</span>
            </div>
          </CardContent>
        </Card>

        {/* Claims Exposure card hidden - not launching with claims functionality */}

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-blue-500" />
              <span className="text-sm text-muted-foreground">Avg Safety Score</span>
            </div>
            <p className="text-2xl font-bold">{summary.avg_safety_score || 0}%</p>
            <Progress value={summary.avg_safety_score || 0} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4 text-purple-500" />
              <span className="text-sm text-muted-foreground">Avg Risk Score</span>
            </div>
            <p className={`text-2xl font-bold ${getRiskColor(summary.avg_risk_score || 0)}`}>
              {(summary.avg_risk_score || 0).toFixed(1)}/10
            </p>
            <Badge className={getRiskBadge(summary.avg_risk_score || 0).color + ' mt-2'}>
              {getRiskBadge(summary.avg_risk_score || 0).label} Risk
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-yellow-500" />
              <span className="text-sm text-muted-foreground">Open Alerts</span>
            </div>
            <p className="text-2xl font-bold">{summary.open_alerts || 0}</p>
            <p className="text-xs text-muted-foreground mt-2">
              {summary.critical_alerts || 0} critical
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different views */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="organizations">Organizations ({orgMetrics.length})</TabsTrigger>
          <TabsTrigger value="incidents">Incidents</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Risk Distribution Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="w-5 h-5" />
                  Risk Distribution
                </CardTitle>
                <CardDescription>Organizations by risk level</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={riskDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {riskDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Incidents by Org Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Incidents by Organization
                </CardTitle>
                <CardDescription>Top 10 organizations by incident count</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={incidentsByOrg} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="incidents" fill="#6366f1" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* High Risk Organizations */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Organizations Requiring Attention
              </CardTitle>
              <CardDescription>Organizations with high risk scores or open incidents</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {sortedOrgMetrics.filter(o => (o.risk_score || 0) > 5 || (o.open_incidents || 0) > 0).slice(0, 5).map((org) => {
                  const riskBadge = getRiskBadge(org.risk_score || 0);
                  const levelColors = LEVEL_COLORS[org.level] || LEVEL_COLORS.local;
                  
                  return (
                    <div 
                      key={org.id}
                      className="flex items-center justify-between p-3 rounded-lg border hover:border-primary/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/org-iq?org=${org.id}`)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${levelColors.bg} flex items-center justify-center`}>
                          <Building2 className={`w-5 h-5 ${levelColors.text}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{org.name}</span>
                            <Badge variant="outline" className={`text-[10px] uppercase ${levelColors.bg} ${levelColors.text}`}>
                              {org.level}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                            {org.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {org.city}, {org.state}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-medium">{org.open_incidents || 0} open incidents</p>
                          <p className="text-xs text-muted-foreground">{org.total_incidents || 0} total</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-bold ${getRiskColor(org.risk_score || 0)}`}>
                            {(org.risk_score || 0).toFixed(1)}
                          </p>
                          <Badge className={riskBadge.color + ' text-[10px]'}>
                            {riskBadge.label}
                          </Badge>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </div>
                  );
                })}
                
                {sortedOrgMetrics.filter(o => (o.risk_score || 0) > 5 || (o.open_incidents || 0) > 0).length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
                    <p>All organizations are in good standing</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Organizations Tab */}
        <TabsContent value="organizations">
          <Card>
            <CardHeader>
              <CardTitle>All Organizations</CardTitle>
              <CardDescription>Complete list of organizations in your network</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Organization</TableHead>
                      <TableHead>Level</TableHead>
                      <TableHead className="text-center">Safety Score</TableHead>
                      <TableHead className="text-center">Risk Score</TableHead>
                      <TableHead className="text-center">Incidents</TableHead>
                      {/* Claims column hidden - not launching with claims functionality */}
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedOrgMetrics.map((org) => {
                      const riskBadge = getRiskBadge(org.risk_score || 0);
                      const levelColors = LEVEL_COLORS[org.level] || LEVEL_COLORS.local;
                      
                      return (
                        <TableRow key={org.id} className="cursor-pointer hover:bg-secondary/50" onClick={() => navigate(`/org-iq?org=${org.id}`)}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg ${levelColors.bg} flex items-center justify-center`}>
                                <Building2 className={`w-4 h-4 ${levelColors.text}`} />
                              </div>
                              <div>
                                <p className="font-medium">{org.name}</p>
                                {org.city && (
                                  <p className="text-xs text-muted-foreground">{org.city}, {org.state}</p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`uppercase text-[10px] ${levelColors.bg} ${levelColors.text}`}>
                              {org.level}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-2">
                              <span className="font-medium">{org.safety_score || 0}%</span>
                              <Progress value={org.safety_score || 0} className="w-16" />
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={riskBadge.color}>
                              {(org.risk_score || 0).toFixed(1)} - {riskBadge.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={org.open_incidents > 0 ? 'text-orange-500 font-medium' : ''}>
                              {org.open_incidents || 0} / {org.total_incidents || 0}
                            </span>
                          </TableCell>
                          {/* Claims column hidden */}
                          <TableCell>
                            <Button variant="ghost" size="sm">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Incidents Tab */}
        <TabsContent value="incidents">
          <Card>
            <CardHeader>
              <CardTitle>Network Incidents Summary</CardTitle>
              <CardDescription>Incident breakdown across all organizations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    By Status
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(summary.incidents_by_status || {}).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between p-2 rounded bg-secondary/30">
                        <span className="capitalize">{status.replace('_', ' ')}</span>
                        <Badge variant="outline">{count}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    By Severity
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(summary.incidents_by_severity || {}).map(([severity, count]) => (
                      <div key={severity} className="flex items-center justify-between p-2 rounded bg-secondary/30">
                        <span className="capitalize">{severity}</span>
                        <Badge variant={severity === 'critical' ? 'destructive' : 'outline'}>{count}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    By Category
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(summary.incidents_by_category || {}).slice(0, 5).map(([category, count]) => (
                      <div key={category} className="flex items-center justify-between p-2 rounded bg-secondary/30">
                        <span className="capitalize truncate">{category.replace('_', ' ')}</span>
                        <Badge variant="outline">{count}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance">
          <Card>
            <CardHeader>
              <CardTitle>Network Compliance Status</CardTitle>
              <CardDescription>Assessment and policy compliance across organizations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="font-medium">Assessments Complete</span>
                  </div>
                  <p className="text-3xl font-bold text-green-500">
                    {summary.assessments_complete || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    of {organizations.length} organizations
                  </p>
                </div>
                
                <div className="p-4 rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-yellow-500" />
                    <span className="font-medium">Assessments Due</span>
                  </div>
                  <p className="text-3xl font-bold text-yellow-500">
                    {summary.assessments_due || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    renewal window open
                  </p>
                </div>
                
                <div className="p-4 rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="w-5 h-5 text-red-500" />
                    <span className="font-medium">Assessments Expired</span>
                  </div>
                  <p className="text-3xl font-bold text-red-500">
                    {summary.assessments_expired || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    need immediate attention
                  </p>
                </div>
                
                <div className="p-4 rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-5 h-5 text-blue-500" />
                    <span className="font-medium">Avg Policy Adoption</span>
                  </div>
                  <p className="text-3xl font-bold text-blue-500">
                    {summary.avg_policy_adoption || 0}%
                  </p>
                  <Progress value={summary.avg_policy_adoption || 0} className="mt-2" />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
